import * as React from 'react';
import { X, ChevronDown } from 'lucide-react';
import { cn } from '../lib/utils.js';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from './dropdown-menu.js';
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from './tooltip.js';

// ─── Types ────────────────────────────────────────────────────

export interface BulkAction {
  id: string;
  label: string;
  icon?: React.ReactNode;
  /** @default 'default' */
  variant?: 'default' | 'destructive';
  disabled?: boolean;
  /**
   * Tooltip text explaining why the action is disabled. Only rendered while
   * `disabled` is true; ignored otherwise. Omit when no explanation is needed.
   */
  disabledTooltip?: string;
  onClick: () => void;
}

export interface BulkActionToolbarProps {
  /** Toolbar is hidden (animated out) when this is 0. */
  selectedCount: number;
  actions: BulkAction[];
  onClearSelection: () => void;
  /** Maximum visible action buttons before the rest collapse into the overflow menu. @default 4 */
  maxVisibleActions?: number;
  className?: string;
  /** Label for the overflow trigger button. */
  moreLabel: string;
  /** Label or function producing the selected-count suffix. */
  selectedLabel: string | ((count: number) => string);
  /**
   * Count of the selected items also present in the current results. When
   * provided and lower than `selectedCount`, a second line names how many
   * selected items the current results don't show, using `visibleLabel`.
   */
  visibleCount?: number;
  /** Label or function producing the visible-count line text. Ignored unless `visibleCount` is set. */
  visibleLabel?: string | ((count: number) => string);
  /** aria-label for the toolbar element. */
  'aria-label': string;
  /** aria-label and tooltip text for the clear-selection button. */
  clearLabel: string;
  /** Optional stable test id override for feature-specific toolbars. */
  testId?: string;
}

// ─── Action button ─────────────────────────────────────────────

interface ActionBtnProps {
  action: BulkAction;
}

function ActionBtn({ action }: ActionBtnProps) {
  const button = (
    <button
      type="button"
      data-testid={`bulk-action-${action.id}`}
      onClick={action.onClick}
      disabled={action.disabled}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] font-medium transition-colors whitespace-nowrap',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40',
        'disabled:pointer-events-none disabled:opacity-50',
        action.variant === 'destructive'
          ? 'text-red-300 hover:bg-red-700/50'
          : 'text-white hover:bg-zinc-700',
      )}
    >
      {action.icon != null && (
        <span className="shrink-0 [&>svg]:h-3.5 [&>svg]:w-3.5">{action.icon}</span>
      )}
      {action.label}
    </button>
  );

  if (action.disabled && action.disabledTooltip != null) {
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          {/* span wrapper required: disabled buttons don't fire pointer events */}
          <TooltipTrigger asChild>
            <span tabIndex={0}>{button}</span>
          </TooltipTrigger>
          <TooltipContent>{action.disabledTooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return button;
}

// ─── Overflow menu ─────────────────────────────────────────────

interface OverflowMenuProps {
  actions: BulkAction[];
  moreLabel: string;
}

function OverflowMenu({ actions, moreLabel }: OverflowMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="bulk-action-overflow-trigger"
          className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-[13px] font-medium text-white hover:bg-zinc-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
        >
          {moreLabel}
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" side="top" className="mb-1 min-w-[160px]">
        {actions.map((action) => (
          <DropdownMenuItem
            key={action.id}
            onClick={action.disabled ? undefined : action.onClick}
            data-testid={`bulk-action-overflow-${action.id}`}
            className={cn(
              action.disabled && 'pointer-events-none opacity-50',
              action.variant === 'destructive' && 'text-destructive focus:text-destructive',
            )}
          >
            {action.icon != null && <span className="[&>svg]:h-4 [&>svg]:w-4">{action.icon}</span>}
            {action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── Separator ────────────────────────────────────────────────

function ToolbarSep() {
  return <div className="w-px h-5 bg-zinc-700 shrink-0 mx-0.5" />;
}

// ─── Component ───────────────────────────────────────────────

export function BulkActionToolbar({
  selectedCount,
  actions,
  onClearSelection,
  maxVisibleActions = 4,
  className,
  moreLabel,
  selectedLabel,
  visibleCount,
  visibleLabel,
  'aria-label': ariaLabel,
  clearLabel,
  testId = 'bulk-action-toolbar',
}: BulkActionToolbarProps) {
  const visible = selectedCount > 0;

  const visibleActions = actions.slice(0, maxVisibleActions);
  const overflowActions = actions.slice(maxVisibleActions);

  const resolvedSelectedLabel =
    typeof selectedLabel === 'function' ? selectedLabel(selectedCount) : selectedLabel;
  const showHiddenCount = visibleCount !== undefined && visibleCount < selectedCount;
  const resolvedVisibleLabel =
    typeof visibleLabel === 'function' ? visibleLabel(visibleCount ?? 0) : visibleLabel;

  return (
    <div
      role="toolbar"
      aria-label={ariaLabel}
      data-testid={testId}
      data-visible={visible}
      className={cn(
        'fixed bottom-6 left-1/2 -translate-x-1/2 z-50',
        // Constrain to the viewport (minus a small gutter) and wrap onto multiple
        // centered rows on narrow screens so actions never overflow off-screen or
        // get clipped by the viewport edge.
        'flex flex-wrap items-center justify-center gap-1 rounded-xl bg-zinc-900 text-white shadow-2xl px-3 py-2',
        'max-w-[calc(100vw-2rem)]',
        'transition-all duration-200 ease-out',
        visible
          ? 'translate-y-0 opacity-100 pointer-events-auto'
          : 'translate-y-4 opacity-0 pointer-events-none',
        className,
      )}
    >
      {/* Count */}
      <div className="flex flex-col pl-1 pr-2">
        <span className="text-[13px] text-zinc-400 whitespace-nowrap">
          <span className="font-semibold text-white">{selectedCount}</span> {resolvedSelectedLabel}
        </span>
        {showHiddenCount ? (
          <span
            className="text-[11px] text-zinc-500 whitespace-nowrap"
            data-testid="bulk-action-toolbar-visible-count"
          >
            {visibleCount} {resolvedVisibleLabel}
          </span>
        ) : null}
      </div>

      <ToolbarSep />

      {/* Visible actions */}
      {visibleActions.map((action) => (
        <ActionBtn key={action.id} action={action} />
      ))}

      {/* Overflow menu */}
      {overflowActions.length > 0 && (
        <>
          <ToolbarSep />
          <OverflowMenu actions={overflowActions} moreLabel={moreLabel} />
        </>
      )}

      <ToolbarSep />

      {/* Clear selection */}
      <TooltipProvider delayDuration={400}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              data-testid="bulk-action-clear"
              onClick={onClearSelection}
              aria-label={clearLabel}
              className="rounded-lg p-1.5 text-zinc-400 hover:bg-zinc-700 hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
            >
              <X className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">{clearLabel}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
